import { useCallback, useMemo, useState } from 'react';
import { CourierFileFilters } from '../models';

/** Filtros de listado sin `page` (la paginación la gestiona `useInfiniteQuery`) */
export type CourierFileListFilters = Omit<CourierFileFilters, 'page'>;

interface UseCourierFilesMainProps {
  initialFilters?: CourierFileListFilters;
}

// Mapeo de campos de AG-Grid a campos de API
const FIELD_TO_API_SORT: Record<string, string> = {
  file_date: 'file_date',
  total_declared: 'total_declared',
  imported_at: 'imported_at',
};

export const useCourierFilesMain = ({ initialFilters = {} }: UseCourierFilesMainProps = {}) => {
  const [filters, setFilters] = useState<CourierFileListFilters>({
    per_page: 15,
    sort_by: 'file_date',
    sort_direction: 'desc',
    ...initialFilters,
  });

  const [sorting, setSorting] = useState<{ field: string; direction: 'asc' | 'desc' } | null>({
    field: 'file_date',
    direction: 'desc',
  });

  const [selectedFiles, setSelectedFiles] = useState<Set<number>>(new Set());

  // Convertir sorting de AG-Grid a filtros de API
  const apiFilters = useMemo(() => {
    const apiField = sorting?.field ? FIELD_TO_API_SORT[sorting.field] || sorting.field : undefined;
    const apiDirection = sorting?.direction;

    // Filtros base sin sort_by y sort_direction (el orden lo aplica `sorting` vía API)
    const baseFilters = { ...filters };
    delete baseFilters.sort_by;
    delete baseFilters.sort_direction;

    return {
      ...baseFilters,
      ...(apiField ? { sort_by: apiField } : {}),
      ...(apiDirection ? { sort_direction: apiDirection } : {}),
    };
  }, [filters, sorting]);

  const updateFilters = (newFilters: Partial<CourierFileListFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };

  // Handler para búsqueda por nombre de archivo
  const handleSearch = useCallback((searchValue: string) => {
    setFilters((prev) => ({
      ...prev,
      search: searchValue || undefined,
    }));
  }, []);

  const resetFilters = () => {
    setFilters({
      per_page: 15,
      sort_by: 'file_date',
      sort_direction: 'desc',
    });
    setSorting({
      field: 'file_date',
      direction: 'desc',
    });
  };

  const changePageSize = (per_page: number) => {
    setFilters((prev) => ({ ...prev, per_page }));
  };

  const handleSortingChange = (newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
    setSorting(newSorting);
    setFilters((prev) => {
      const next = { ...prev };
      delete next.sort_by;
      delete next.sort_direction;
      return next;
    });
  };

  // Manejo de selección
  const handleSelectionChanged = useCallback((selectedIds: Set<number>) => {
    setSelectedFiles(selectedIds);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedFiles(new Set());
  }, []);

  return {
    filters: apiFilters,
    updateFilters,
    resetFilters,
    changePageSize,
    handleSortingChange,
    handleSearch,
    searchTerm: filters.search || '',
    sorting,
    selectedFiles,
    handleSelectionChanged,
    handleClearSelection,
  };
};
